import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  getCompanyPlanAsAdmin,
  getSubscription,
  grantPlan,
  listAllCompaniesForPlanAdmin,
  revokePlanGrant,
} from "../../services/api/billingApi";
import { CompanyLimitsEditor } from "./CompanyLimitsEditor";

const PLAN_OPTIONS = [
  { value: "starter", label: "Starter" },
  { value: "essential", label: "Essential" },
  { value: "pro", label: "Pro" },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return String(iso);
  }
}

const ACCOUNT_TYPES = {
  grant: { label: "Cortesia", className: "account-badge account-badge--grant" },
  stripe: { label: "Pago", className: "account-badge account-badge--paid" },
  trial: { label: "Trial", className: "account-badge account-badge--trial" },
  free: { label: "Grátis", className: "account-badge account-badge--free" },
};

function AccountTypeBadge({ source }) {
  const type = ACCOUNT_TYPES[source];
  if (!type) return <span className="muted-text">{source || "—"}</span>;
  return <span className={type.className}>{type.label}</span>;
}

function defaultRowDraft() {
  return { durationDays: "30", planCode: "starter", reason: "" };
}

export function CompanyCourtesyPanel() {
  const { companies: linkedCompanies } = useAuth();
  /** null = ainda não sabe; true = admin de billing (vê todas as empresas). */
  const [planAdmin, setPlanAdmin] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const companies = planAdmin ? allCompanies : linkedCompanies;
  const [query, setQuery] = useState("");
  const [subscriptions, setSubscriptions] = useState({});
  const [rowDrafts, setRowDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyCompanyId, setBusyCompanyId] = useState(null);
  const [limitsCompanyId, setLimitsCompanyId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const filteredCompanies = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return companies;
    return companies.filter((company) => {
      const name = (company?.name || "").toLowerCase();
      const id = String(company?.id || "").toLowerCase();
      return name.includes(normalized) || id.includes(normalized);
    });
  }, [companies, query]);

  const loadSubscriptions = useCallback(async (companyList) => {
    setLoading(true);
    setError("");
    let adminList = null;
    try {
      adminList = await listAllCompaniesForPlanAdmin();
    } catch (e) {
      setError(e?.message || "Erro ao carregar empresas.");
    }
    if (adminList) {
      setPlanAdmin(true);
      setAllCompanies(adminList.map((c) => ({ id: c.id, name: c.name })));
      setSubscriptions(
        Object.fromEntries(adminList.map((c) => [c.id, { status: "ok", data: c }]))
      );
      setLoading(false);
      return;
    }
    setPlanAdmin(false);
    if (!companyList.length) {
      setSubscriptions({});
      setLoading(false);
      return;
    }
    const results = await Promise.all(
      companyList.map(async (company) => {
        try {
          const sub = await getSubscription(company.id);
          return [company.id, { status: "ok", data: sub }];
        } catch (e) {
          return [company.id, { status: "error", message: e?.message || "Erro ao carregar" }];
        }
      })
    );
    setSubscriptions(Object.fromEntries(results));
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadSubscriptions(linkedCompanies);
  }, [linkedCompanies, loadSubscriptions]);

  useEffect(() => {
    setRowDrafts((prev) => {
      const next = { ...prev };
      companies.forEach((company) => {
        if (!next[company.id]) next[company.id] = defaultRowDraft();
      });
      return next;
    });
  }, [companies]);

  const updateDraft = (companyId, field, value) => {
    setRowDrafts((prev) => ({
      ...prev,
      [companyId]: { ...(prev[companyId] || defaultRowDraft()), [field]: value },
    }));
  };

  const refreshCompany = async (companyId) => {
    try {
      const sub = planAdmin ? await getCompanyPlanAsAdmin(companyId) : await getSubscription(companyId);
      setSubscriptions((prev) => ({
        ...prev,
        [companyId]: { status: "ok", data: sub },
      }));
    } catch (e) {
      setSubscriptions((prev) => ({
        ...prev,
        [companyId]: { status: "error", message: e?.message || "Erro ao carregar" },
      }));
    }
  };

  const handleGrant = async (company) => {
    const draft = rowDrafts[company.id] || defaultRowDraft();
    const durationDays = Number.parseInt(String(draft.durationDays), 10);
    if (!Number.isFinite(durationDays) || durationDays < 1 || durationDays > 365) {
      setError("Informe entre 1 e 365 dias de cortesia.");
      setMessage("");
      return;
    }

    setBusyCompanyId(company.id);
    setError("");
    setMessage("");
    try {
      const result = await grantPlan({
        companyId: company.id,
        planCode: draft.planCode,
        durationDays,
        reason: draft.reason,
      });
      await refreshCompany(company.id);
      setMessage(
        `Cortesia concedida para "${company.name}" (${PLAN_OPTIONS.find((p) => p.value === draft.planCode)?.label || draft.planCode}, ${durationDays} dias). Expira em ${formatDate(result?.expiresAt)}.`
      );
    } catch (e) {
      setError(e?.message || "Não foi possível conceder cortesia.");
      setMessage("");
    } finally {
      setBusyCompanyId(null);
    }
  };

  const handleRevoke = async (company) => {
    setBusyCompanyId(company.id);
    setError("");
    setMessage("");
    try {
      await revokePlanGrant(company.id);
      await refreshCompany(company.id);
      setMessage(`Cortesia revogada para "${company.name}".`);
    } catch (e) {
      setError(e?.message || "Não foi possível revogar cortesia.");
      setMessage("");
    } finally {
      setBusyCompanyId(null);
    }
  };

  if (!loading && !companies.length) {
    return (
      <section className="card">
        <h2>Cortesia de planos</h2>
        <p>Seu usuário não possui empresas vinculadas no momento.</p>
      </section>
    );
  }

  return (
    <section className="card courtesy-panel">
      <div className="section-header">
        <div>
          <h2>Cortesia de planos</h2>
          <p className="section-description" style={{ marginBottom: 0 }}>
            {planAdmin
              ? "Conceda dias de plano gratuito (cortesia) para qualquer empresa da plataforma."
              : "Conceda dias de plano gratuito (cortesia) para as empresas vinculadas ao seu usuário de suporte."}
          </p>
        </div>
        <span className="section-badge">{filteredCompanies.length} empresa(s)</span>
      </div>

      <div className="courtesy-toolbar">
        <input
          className="search-input"
          placeholder="Buscar por nome ou ID..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button
          className="secondary-button"
          type="button"
          onClick={() => void loadSubscriptions(linkedCompanies)}
          disabled={loading}
        >
          Atualizar lista
        </button>
      </div>

      {message ? <div className="success-box">{message}</div> : null}
      {error ? <div className="error-box">{error}</div> : null}

      {loading ? (
        <p className="muted-text">Carregando assinaturas...</p>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Plano atual</th>
                <th>Tipo de conta</th>
                <th>Cortesia até</th>
                <th>Dias</th>
                <th>Plano cortesia</th>
                <th>Motivo</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => {
                const subEntry = subscriptions[company.id];
                const sub = subEntry?.status === "ok" ? subEntry.data : null;
                const draft = rowDrafts[company.id] || defaultRowDraft();
                const isGrant = sub?.effectivePlanSource === "grant";
                const isBusy = busyCompanyId === company.id;

                const limitsOpen = limitsCompanyId === company.id;

                return (
                  <Fragment key={company.id}>
                  <tr>
                    <td>
                      <strong>{company.name}</strong>
                      <div className="muted-text table-sub">{company.id}</div>
                    </td>
                    <td>{sub?.effectivePlanCode || "—"}</td>
                    <td>
                      {subEntry?.status === "error" ? "Erro" : <AccountTypeBadge source={sub?.effectivePlanSource} />}
                    </td>
                    <td>{isGrant ? formatDate(sub?.grantExpiresAt) : "—"}</td>
                    <td>
                      <input
                        className="table-input"
                        type="number"
                        min={1}
                        max={365}
                        value={draft.durationDays}
                        onChange={(event) => updateDraft(company.id, "durationDays", event.target.value)}
                        disabled={isBusy}
                      />
                    </td>
                    <td>
                      <select
                        className="table-input"
                        value={draft.planCode}
                        onChange={(event) => updateDraft(company.id, "planCode", event.target.value)}
                        disabled={isBusy}
                      >
                        {PLAN_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="table-input table-input--wide"
                        type="text"
                        placeholder="Opcional"
                        value={draft.reason}
                        onChange={(event) => updateDraft(company.id, "reason", event.target.value)}
                        disabled={isBusy}
                      />
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="primary-button"
                          type="button"
                          onClick={() => void handleGrant(company)}
                          disabled={isBusy}
                        >
                          {isBusy ? "..." : "Conceder"}
                        </button>
                        {isGrant ? (
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => void handleRevoke(company)}
                            disabled={isBusy}
                          >
                            Revogar
                          </button>
                        ) : null}
                        {planAdmin ? (
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => setLimitsCompanyId(limitsOpen ? null : company.id)}
                          >
                            {limitsOpen ? "Fechar limites" : "Limites"}
                          </button>
                        ) : null}
                      </div>
                      {subEntry?.status === "error" ? (
                        <div className="muted-text table-sub">{subEntry.message}</div>
                      ) : null}
                    </td>
                  </tr>
                  {limitsOpen ? (
                    <tr className="limits-editor-row">
                      <td colSpan={8}>
                        <CompanyLimitsEditor company={company} />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
